import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, ErrorMsg, Text } from '@fragments';
import { IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { LIMITS } from '../../../../../enums/automations';
import { Field, OptionCard, ToggleCard } from './parts';

const normalize = word => word.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Comma or Enter makes a chip; the comparison ignores case, accents and extra spaces (as core does).
function KeywordInput({ keywords, onChange, error }) {
  const { t } = useTranslation('studioAutomations');
  const [typing, setTyping] = useState('');
  const add = text => {
    const words = text.split(',').map(word => word.trim().slice(0, LIMITS.keyword)).filter(Boolean);
    const next = [...keywords];
    words.forEach(word => { if (next.length < LIMITS.keywords && !next.some(item => normalize(item) === normalize(word))) next.push(word); });
    onChange(next);
    setTyping('');
  };
  const input = next => { if (next.includes(',')) add(next); else setTyping(next); };
  const suggestions = t('keywordSuggestions', { returnObjects: true });
  return <div className="studio-automation-keywords">
    <Field label={t('keywordLabel')} value={typing} onChange={input} placeholder={t('keywordPlaceholder')} error={error} helper={t('keywordHelper')} inputMode="text"
      onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); add(typing); } else if (event.key === 'Backspace' && !typing && keywords.length) onChange(keywords.slice(0, -1)); }}
      onBlur={() => { if (typing.trim()) add(typing); }} />
    {keywords.length > 0 && <div className="studio-automation-keywords__chips">
      {keywords.map(word => <span key={word} className="studio-automation-keywords__chip"><Text.span contrast={8}>{word}</Text.span>
        <button type="button" aria-label={t('keywordRemove', { word })} onClick={() => onChange(keywords.filter(item => item !== word))}><IconX /></button></span>)}
    </div>}
    {!keywords.length && Array.isArray(suggestions) && <div className="studio-automation-keywords__suggestions">
      <Text.caption contrast={6}>{t('keywordExamples')}</Text.caption>
      {suggestions.map(word => <Button key={word} size="micro" variant="rounded" color="overlay" preventLoading onClick={() => add(word)}>{word}</Button>)}
    </div>}
  </div>;
}

// "E o comentário tiver": which words fire it and, on comments, the public reply.
export default function KeywordStep({ editor }) {
  const { t } = useTranslation('studioAutomations');
  const { draft, errors, update } = editor;
  const { trigger, comment_reply: reply } = draft;
  const replies = reply.templates;
  const setReply = (index, value) => update('comment_reply.templates', replies.map((item, position) => (position === index ? value : item)));
  return <div className="studio-automation-step__fields">
    <div role="radiogroup" aria-label={t('keywordsMatch')} className="studio-automation-step__options">
      <OptionCard selected={trigger.match !== 'any'} onSelect={() => update('trigger.match', 'word')} title={t('matchKeyword')}>
        <KeywordInput keywords={trigger.keywords} onChange={value => update('trigger.keywords', value)} error={errors['trigger.keywords']?.[0]} />
        <ButtonGroup size="small" value={trigger.match} onChange={value => update('trigger.match', value)}
          options={[{ value: 'word', label: t('matchWord') }, { value: 'exact', label: t('matchExact') }]} />
      </OptionCard>
      <OptionCard selected={trigger.match === 'any'} onSelect={() => { update('trigger.match', 'any'); update('trigger.keywords', []); }} title={t('matchAny')} hint={t('matchAnyHint')} />
    </div>
    {trigger.channel === 'comments' && <ToggleCard on={reply.enabled} onChange={value => { update('comment_reply.enabled', value); if (value && !replies.length) update('comment_reply.templates', ['']); }} title={t('replyTitle')} hint={t('replyHint')}>
      {replies.map((text, index) => <div key={index} className="studio-automation-reply">
        <Field ariaLabel={t('replyLabel', { number: index + 1 })} value={text} onChange={value => setReply(index, value)} max={LIMITS.reply} placeholder={t('replyPlaceholder')} />
        {replies.length > 1 && <Button variant="text" color="overlay" size="small" icon={<IconTrash />} aria-label={t('replyRemove')} tooltip={t('replyRemove')} preventLoading onClick={() => update('comment_reply.templates', replies.filter((_, position) => position !== index))} />}
      </div>)}
      {errors['comment_reply.templates'] && <ErrorMsg>{errors['comment_reply.templates'][0]}</ErrorMsg>}
      {replies.length < LIMITS.replies && <Button variant="dashed" color="overlay" fullWidth startIcon={<IconPlus />} preventLoading onClick={() => update('comment_reply.templates', [...replies, ''])}>{t('replyAdd')}</Button>}
    </ToggleCard>}
  </div>;
}
